import { filter, tap, switchMap } from 'rxjs/operators';
import { Component, Input, OnInit } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl
} from '@angular/forms';
import { Observable, merge, of, Subject } from 'rxjs';
//import { MatDialog } from '@angular/material/dialog';

import * as fromRoot from '../../reducers';
import * as forms from '../../actions/forms';
import * as support from '../../actions/support';
import * as utils from '../../shared/utils';
import * as notifications from '../../actions/notifications';

@Component({
  selector: 'edge-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss']
})
export class CreateUserComponent implements OnInit {
  @Input() sport: string = '';
  createUserForm: FormGroup;

  formName = 'createUser';
  submitted = false;
  formattedPhone: string = '';
  maxPhoneNumberLimit = 17;
  currentPhoneNumberLength: number = 0;
  previousPhoneNumberLength: number = 0;
  years = new Array(125);
  months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  armyInterestOptions = [
    { value: 'Very Interested', selectionNumber: 1 },
    { value: 'Somewhat Interested', selectionNumber: 2 },
    { value: 'Not Interested', selectionNumber: 3 },
    { value: `Don't know enough about the Army to say`, selectionNumber: 4 },
    { value: 'Currently in the military', selectionNumber: 5 },
    { value: 'Retired Military', selectionNumber: 6 }
  ];

  validationMessages: any = {
    sport: { required: 'Please indicate a sport' },
    userName: {
      required: 'Please provide a user name'
    },
    emailAddress: {
      required: 'Please provide your email address',
      email: 'Please provide a valid email address'
    },
    password: {
      required: 'Please provide your password.',
      minlength: 'Password should be 8-15 characters',
      maxlength: 'Password should be 8-15 characters'
    },
    passwordConfirmation: {
      required: 'Password Confirmation is required',
      nomatch: 'Password Confirmation must match Password'
    },
    birthdayMonth: { required: 'Please select your birth month' },
    birthdayYear: { required: 'Please select your birth year' },
    armyInterest: { required: 'Please select your interest in the U.S. Army' },
    phoneNumber: {
      required: 'Please provide a 10 digit phone number',
      maxlength: 'Please use the correct format (888) 550-2769',
      minlength: 'Please use the correct format (888) 550-2769'
    }
  };

  action$ = new Subject();
  formAction$: Observable<Action> = this.action$.pipe(
    tap(() => (this.submitted = true)),
    switchMap((data) =>
      of(forms.FormRequestAction(utils.fromForm(this.formName, data))).pipe(
        filter(() => this.createUserForm.valid)
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private formBuilder: FormBuilder
    //public dialog: MatDialog,
  ) {
    merge(this.formAction$).subscribe(store);
    store.dispatch(forms.FormCreateAction({ formName: this.formName }));
    this.buildVerifyForm();
  }

  buildVerifyForm(): void {
    this.createUserForm = this.formBuilder.group({
      sport: [{ value: '', disabled: true }, Validators.required],
      userName: ['', Validators.required],
      emailAddress: [
        '',
        Validators.compose([Validators.required, Validators.email])
      ],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(15)
        ])
      ],
      passwordConfirmation: [
        '',
        Validators.compose([Validators.required, this.passwordMatcher])
      ],
      birthdayMonth: ['', Validators.required],
      birthdayYear: ['', Validators.required],
      armyInterest: ['', Validators.required],
      phoneNumber: [
        '',
        Validators.compose([
          Validators.required,
          Validators.maxLength(16),
          Validators.minLength(12)
        ])
      ],
      emailOption: [false]
    });

    this.createUserForm.valueChanges.subscribe(() => this.onValueChanged());
    this.onValueChanged();
  }

  onValueChanged(): void {
    if (!this.createUserForm) {
      return;
    }
    const form = this.createUserForm;
    if (
      form.value.password === form.value.passwordConfirmation &&
      form.value.passwordConfirmation !== ''
    ) {
      form.controls.passwordConfirmation.updateValueAndValidity({
        onlySelf: true
      });
    }
  }

  passwordMatcher(control: AbstractControl): any {
    const password = control.root.get('password');
    const confirm = control.root.get('passwordConfirmation');
    if (!password || !confirm) {
      return null;
    }
    return password.value === confirm.value ? null : { nomatch: true };
  }

  hasError(key: string, type: string): boolean {
    const control = this.createUserForm.controls[key];
    return control.errors ? control.errors[type] : false;
  }

  getErrorMessage(key: string, type: string): string {
    return this.validationMessages[key][type];
  }

  onSubmit(): void {
    if (!this.createUserForm.valid) {
      this.store.dispatch(
        notifications.AddFailureNotification(
          'Please fix the form errors and try again.'
        )
      );
    }
    this.action$.next(this.createUserForm.value);
  }

  ngOnInit(): void {
    this.store.dispatch(support.ToggleSupportPageAction(this.sport));
    this.createUserForm.controls.sport.setValue(this.sport);
  }

  formatPhoneNumber(evt: any): void {
    this.currentPhoneNumberLength = evt.target.value.length;
    if (
      this.currentPhoneNumberLength < this.previousPhoneNumberLength &&
      this.currentPhoneNumberLength < this.maxPhoneNumberLimit
    ) {
      this.previousPhoneNumberLength = this.currentPhoneNumberLength;
      return;
    }
    const val = `${evt.target.value}`.replace(/\D/g, '');
    this.formattedPhone = `(${val.substring(0, 3)}) ${val.substring(
      3,
      6
    )} - ${val.substring(6, 10)}`;
    this.previousPhoneNumberLength = this.currentPhoneNumberLength;
  }

  /**
   * Takes in a number and subtracts that from the current year
   * @param yearsPast The amount of years to go back
   * @returns Returns an amount of years from the current year
   */
  getPastYear(yearsPast: number): number {
    return new Date().getFullYear() - yearsPast;
  }
}
